import { isActionOf, getType } from 'typesafe-actions'
import { from, of } from 'rxjs'
import { catchError, exhaustMap, filter, map, mergeMap, switchMap } from 'rxjs/operators'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'
import { RootEpic } from '../types'
import {
  getSfBankAccountsRequest,
  getSfBankAccountsSuccess,
  getSfBankAccountsFailure,
  createSfBankAccountRequest,
  createSfBankAccountSuccess,
  createSfBankAccountFailure,
  updateSfBankAccountRequest,
  updateSfBankAccountSuccess,
  updateSfBankAccountFailure,
  assignSfBankAccountToUserRequest,
  assignSfBankAccountToUserSuccess,
  assignSfBankAccountToUserFailure,
  getAssignedAccountsByUserRequest,
  getAssignedAccountsByUserSuccess,
  getAssignedAccountsByUserFailure,
  unassignSfBankAccountFromUserRequest,
  unassignSfBankAccountFromUserSuccess,
  unassignSfBankAccountFromUserFailure,
  getAssignedAccountIdsByUserRequest,
  getAssignedAccountIdsByUserSuccess,
  getAssignedAccountIdsByUserFailure,
} from '../actions'

export const fetchSfBankAccountsEpic: RootEpic = (action$, state$, { fetchSfBankAccounts }) => {
  return action$.pipe(
    filter(isActionOf([getSfBankAccountsRequest, createSfBankAccountSuccess, updateSfBankAccountSuccess])),
    switchMap(({ type, payload }) =>
      from(
        fetchSfBankAccounts({
          token: state$.value.app.authToken as string,
          limit: 10,
          ...state$.value.sfBankAccounts.filters,
          ...(type === getType(getSfBankAccountsRequest) && payload),
        }),
      ).pipe(
        map((response) => getSfBankAccountsSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch bank accounts.')
          return of(getSfBankAccountsFailure(message))
        }),
      ),
    ),
  )
}

export const createSfBankAccountsEpic: RootEpic = (action$, state$, { createSfBankAccount }) => {
  return action$.pipe(
    filter(isActionOf([createSfBankAccountRequest])),
    exhaustMap(({ payload }) =>
      from(
        createSfBankAccount({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        map((response) => {
          toast.success('Bank account successfully created.')
          return createSfBankAccountSuccess(response)
        }),
        catchError((error: AxiosError) => {
          toast.error(error.response?.data.message || 'Failed to create bank account.')
          return of(createSfBankAccountFailure(error))
        }),
      ),
    ),
  )
}

export const updateSfBankAccountsEpic: RootEpic = (action$, state$, { updateSfBankAccount }) => {
  return action$.pipe(
    filter(isActionOf([updateSfBankAccountRequest])),
    exhaustMap(({ payload }) =>
      from(
        updateSfBankAccount({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        map((response) => {
          toast.success('Bank account successfully updated.')
          return updateSfBankAccountSuccess(response)
        }),
        catchError((error: AxiosError) => {
          toast.error(error.response?.data.message || 'Failed to update bank account.')
          return of(updateSfBankAccountFailure(error))
        }),
      ),
    ),
  )
}

export const getAssignedAccountsByUserEpic: RootEpic = (action$, state$, { getAssignedAccountsByUser }) => {
  return action$.pipe(
    filter(isActionOf([getAssignedAccountsByUserRequest])),
    switchMap(({ payload: { idSf, page, count } }: ReturnType<typeof getAssignedAccountsByUserRequest>) =>
      from(
        getAssignedAccountsByUser({
          token: state$.value.app.authToken as string,
          idSf,
          ...(page && { page }),
          ...(count && { count }),
        }),
      ).pipe(
        mergeMap((response) =>
          !response.items.length && page && page > 1
            ? of(getAssignedAccountsByUserRequest({ idSf, page: page - 1 }))
            : of(getAssignedAccountsByUserSuccess(response)),
        ),
        catchError((message) => of(getAssignedAccountsByUserFailure(message))),
      ),
    ),
  )
}

export const getAssignedAccountIdsByUserEpic: RootEpic = (action$, state$, { getAssignedAccountIdsByUser }) => {
  return action$.pipe(
    filter(isActionOf([getAssignedAccountIdsByUserRequest, getAssignedAccountsByUserRequest])),
    exhaustMap(({ payload: { idSf } }) =>
      from(
        getAssignedAccountIdsByUser({
          token: state$.value.app.authToken as string,
          idSf,
        }),
      ).pipe(
        map((response) => getAssignedAccountIdsByUserSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch bank account ids.')
          return of(getAssignedAccountIdsByUserFailure(message))
        }),
      ),
    ),
  )
}

export const assignSfBankAccountToUserEpic: RootEpic = (action$, state$, { assignSfAccountToUser }) => {
  return action$.pipe(
    filter(isActionOf([assignSfBankAccountToUserRequest])),
    exhaustMap(({ payload }) =>
      from(
        assignSfAccountToUser({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        mergeMap((response) => {
          if (response.success) {
            toast.success('Bank Account has been added successfully')
            return of(
              assignSfBankAccountToUserSuccess(response),
              getAssignedAccountsByUserRequest({
                idSf: payload.userIdSf,
                page: state$.value.sfBankAccounts.assignedUserAccounts.data.meta.currentPage,
              }),
            )
          }
          return of(assignSfBankAccountToUserSuccess(response))
        }),
        catchError((message) => {
          toast.error('Failed: ', message)
          return of(assignSfBankAccountToUserFailure(message))
        }),
      ),
    ),
  )
}

export const unassignSfBankAccountToUserEpic: RootEpic = (action$, state$, { unassignSfAccountFromUser }) => {
  return action$.pipe(
    filter(isActionOf([unassignSfBankAccountFromUserRequest])),
    exhaustMap(({ payload }) =>
      from(
        unassignSfAccountFromUser({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        mergeMap((response) => {
          if (response.success) {
            toast.success('Bank Account has been unassigned successfully')
            return of(
              unassignSfBankAccountFromUserSuccess(response),
              getAssignedAccountsByUserRequest({
                idSf: payload.userIdSf,
                page: state$.value.sfBankAccounts.assignedUserAccounts.data.meta.currentPage,
              }),
            )
          }
          return of(unassignSfBankAccountFromUserSuccess(response))
        }),
        catchError((message) => {
          toast.error('Failed: ', message)
          return of(unassignSfBankAccountFromUserFailure(message))
        }),
      ),
    ),
  )
}

export default [
  fetchSfBankAccountsEpic,
  createSfBankAccountsEpic,
  updateSfBankAccountsEpic,
  getAssignedAccountsByUserEpic,
  getAssignedAccountIdsByUserEpic,
  assignSfBankAccountToUserEpic,
  unassignSfBankAccountToUserEpic,
]
